import { useAuthContext } from 'contexts/AuthContext';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { GetMessagesService } from '../services/chatServices';
import { ChatHeader, FilterMessage, MessagesByDate } from '../types/types';
import { handelShowFile, renderSentMessage } from '../utils/renderSentMessage';
import { ChatMessagesSkeleton } from './ChatSkeletons';
import { getIconByExtension } from 'utils/globalMethodes';
import { useMediaContext } from 'contexts/MediaContext';

interface RenderMessageProps {
  headerInfo: ChatHeader;

  handeldeleteMessage: (messageId: number) => void;
}
const RenderMessage: React.FC<RenderMessageProps> = ({
  headerInfo,
  handeldeleteMessage,
}) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [messages, setMessages] = useState<MessagesByDate[] | null>(null);
  const { openMedia } = useMediaContext();
  const getVueIcon = (mimeType: string): string => {
    return mimeType.includes('pdf') ? 'eye.svg' : 'download.svg';
  };
  const { user } = useAuthContext();
  const [isFetchNewMsg, setIsFetchNewMsg] = useState(false);
  const filterMessage: FilterMessage = {
    searchValue: '',
    lenght: 5,
    currentList: 0,
    id: 0,
    roleName: '',
    userid: '',
    roleId: '',
    ids: [],
    assigend: true,
    selectAll: true,
    isGroup: true,
    isApprovalEvpLevel: true,
  };

  useEffect(() => {
    if (headerInfo) {
      setMessages(null);
      FetchMessage(0);
    }
  }, [headerInfo]);

  const FetchMessage = async (pageIndex: number = 0): Promise<void> => {
    const response = await GetMessagesService({
      ...filterMessage,
      userid: headerInfo.id,
      isGroup: headerInfo.isGroup,
      currentList: pageIndex,
    });

    if (pageIndex === 0) {
      setMessages(response);
      setTimeout(() => {
        scrollToBottom();
      }, 50);
    } else {
      setIsFetchNewMsg(false);
      const container = document.querySelector('.chat-messages-container');
      if (container && response.length > 0) {
        response[0].messages
          .slice()
          .reverse()
          .map((item) => {
            renderSentMessage({
              item: item,
              user: user,
              isRenderMore: true,
              handeldeleteMessage,
              openMedia: openMedia,
            });
          });
      }
    }
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>): void => {
    const target = event.currentTarget;
    if (target.scrollTop < 50 && !isFetchNewMsg) {
      setIsFetchNewMsg(true);
      const nextPage = page + 1;
      setPage(nextPage);
      FetchMessage(nextPage);
    }
  };

  const scrollToBottom = (): void => {
    const containerMessage = document.querySelector('.chat-messages-container');
    if (containerMessage) {
      containerMessage.scrollTop = containerMessage.scrollHeight;
    }
  };

  if (!messages) return <ChatMessagesSkeleton />;
  return (
    <>
      {isFetchNewMsg && (
        <div className="tw-flex tw-items-center tw-justify-center tw-w-full tw-py-2 tw-absolute tw-top-0 tw-left-0 tw-z-10">
          <img
            className="tw-animate-spin tw-w-5 tw-h-5"
            src="/icons/spinner-outer.svg"
            alt="spinner"
          />
        </div>
      )}
      <div
        className="chat-messages-container tw-flex-1 tw-min-h-0 tw-overflow-y-auto tw-custom-scrollbar tw-py-3"
        data-id={headerInfo?.id}
        onScroll={handleScroll}
      >
        {messages && messages?.length > 0 ? (
          messages.map((item, index) => (
            <div id="section-message" data-cpt={index} key={index}>
              <div className="tw-sticky tw-top-2 tw-z-10 tw-flex tw-justify-center tw-my-3">
                <span className="tw-bg-white tw-text-dark-gray4 tw-text-[calc(var(--unit-minthr)*10)] tw-font-semibold tw-capitalize tw-tracking-wide tw-px-3 tw-py-1 tw-rounded-full tw-border tw-border-light-gray3 tw-shadow-sm date-test">
                  {item.date}
                </span>
              </div>
              <div className="tw-flex tw-flex-col tw-gap-4">
                {item.messages?.length > 0 &&
                  item.messages.map((msg, idex) => {
                    const received = msg.user != user.userId;
                    const chatClass = received
                      ? 'chat-received tw-max-w-[85%] tw-min-w-[20%]'
                      : 'chat-sent tw-max-w-[85%] tw-min-w-[20%] tw-ms-auto';

                    return (
                      <div
                        key={idex}
                        className={`${chatClass} ${!msg.seen && !received ? ' tw-group' : ''}`}
                        data-id={msg.id}
                      >
                        <div
                          className={`tw-flex ${received ? 'tw-justify-start tw-pl-7 ' : 'tw-justify-end tw-pr-7'}`}
                        >
                          <div
                            className={`tw-max-w-full tw-flex tw-flex-col ${received ? 'tw-items-start' : 'tw-items-end'}`}
                          >
                            {msg.message && msg.message != '' && (
                              <div
                                className={`tw-ps-3 tw-pe-4 tw-py-2 tw-text-[calc(var(--unit-minthr)*12.5)] tw-leading-snug tw-shadow-sm tw-flex tw-items-end tw-gap-2 ${
                                  received
                                    ? 'tw-bg-white tw-text-dark tw-border tw-border-light-gray tw-rounded-[calc(var(--unit-minthr)*14)] tw-rounded-bl-[calc(var(--unit-minthr)*4)] '
                                    : 'tw-bg-primary tw-text-white tw-rounded-[calc(var(--unit-minthr)*14)] tw-rounded-br-[calc(var(--unit-minthr)*4)]'
                                }`}
                              >
                                <span className="tw-whitespace-pre-wrap tw-break-words tw-text-left">
                                  {msg.message}
                                </span>
                                <span
                                  className={`tw-inline-flex tw-items-center tw-gap-1 tw-shrink-0 tw-translate-y-[1px] tw-text-[calc(var(--unit-minthr)*10)] tw-leading-none ${
                                    received
                                      ? 'tw-text-dark-gray4'
                                      : 'tw-text-white tw-opacity-80'
                                  }`}
                                >
                                  {!msg.seen && !received && (
                                    <button
                                      className="tw-hidden tw-group-hover:tw-inline-flex tw-items-center tw-bg-transparent tw-border-none tw-p-0 tw-cursor-pointer"
                                      onClick={() =>
                                        handeldeleteMessage(msg.id)
                                      }
                                      aria-label={t('delete')}
                                    >
                                      <img
                                        className="tw-w-3 tw-h-3 tw-icon-white"
                                        src="/icons/delete.svg"
                                        alt=""
                                      />
                                    </button>
                                  )}
                                  <span>
                                    {msg.createdDate
                                      ? moment(msg.createdDate).format('HH:mm')
                                      : msg.date}
                                  </span>
                                  {!received && (
                                    <img
                                      aria-hidden
                                      className="tw-w-3.5 tw-h-3.5 tw-icon-white tw-opacity-90"
                                      src={
                                        msg.seen
                                          ? '/icons/read.svg'
                                          : '/icons/check.svg'
                                      }
                                      alt=""
                                    />
                                  )}
                                </span>
                              </div>
                            )}

                            {msg.files?.length > 0 &&
                              msg.files.map((file, fIndex) => (
                                <div
                                  key={fIndex}
                                  className={`tw-mt-2 tw-rounded-2xl tw-overflow-hidden ${
                                    file.mimeType.includes('image')
                                      ? 'vue-image'
                                      : file.mimeType.includes('pdf')
                                        ? 'vue-pdf'
                                        : 'DownLoadFile'
                                  } ${
                                    received
                                      ? 'tw-bg-white tw-text-dark'
                                      : 'tw-bg-primary tw-text-white'
                                  }`}
                                >
                                  <a
                                    onClick={() => {
                                      handelShowFile(
                                        file.name,
                                        file.id,
                                        file.mimeType,
                                        openMedia,
                                      );
                                    }}
                                    data-name={file.name}
                                    data-id={file.id}
                                    data-mimeType={file.mimeType}
                                    className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-no-underline tw-cursor-pointer"
                                  >
                                    {file.mimeType.includes('image') ? (
                                      <img
                                        className="tw-rounded-2xl tw-max-w-full tw-block"
                                        src={`/upload/GetChatDocument/${file.id}`}
                                        alt={file.name}
                                      />
                                    ) : (
                                      <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-w-full">
                                        <img
                                          className="tw-w-6 tw-h-6 tw-shrink-0"
                                          src={`/icons/${getIconByExtension(file.path)}`}
                                          alt=""
                                        />
                                        <div className="tw-flex-1 tw-min-w-0">
                                          <h6 className="tw-truncate tw-text-sm tw-font-medium tw-m-0">
                                            {file.name}
                                          </h6>
                                        </div>
                                        <img
                                          className={`tw-w-4 tw-h-4 tw-shrink-0 ${received ? 'tw-icon-gray' : 'tw-icon-white'}`}
                                          src={`/icons/${getVueIcon(file.mimeType)}`}
                                          alt=""
                                        />
                                      </div>
                                    )}
                                  </a>
                                </div>
                              ))}

                            {(!msg.message || msg.message === '') && (
                              <div
                                className={`tw-mt-1 tw-px-1 tw-flex tw-items-center tw-gap-1.5 tw-text-[calc(var(--unit-minthr)*10)] tw-text-dark-gray4 ${
                                  received
                                    ? 'tw-justify-start '
                                    : 'tw-justify-end '
                                }`}
                              >
                                {!msg.seen && !received && (
                                  <button
                                    className="tw-bg-transparent tw-border-none tw-text-danger tw-text-[calc(var(--unit-minthr)*10)] tw-font-medium tw-p-0 tw-cursor-pointer tw-hidden tw-group-hover:tw-flex"
                                    onClick={() => {
                                      handeldeleteMessage(msg.id);
                                    }}
                                  >
                                    {t('delete')}
                                  </button>
                                )}
                                <span>
                                  {msg.createdDate
                                    ? moment(msg.createdDate).format('HH:mm')
                                    : msg.date}
                                </span>
                                {!received && (
                                  <img
                                    aria-hidden
                                    className={`tw-w-3.5 tw-h-3.5 ${msg.seen ? 'tw-icon-info' : 'tw-icon-gray'}`}
                                    src={
                                      msg.seen
                                        ? '/icons/read.svg'
                                        : '/icons/check.svg'
                                    }
                                    alt=""
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          ))
        ) : (
          <div className="empty-conversation tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-full tw-text-center tw-p-10 ">
            <img
              className="tw-w-23 tw-h-20 tw-icon-gray3"
              src="/icons/empty-chat.svg"
              alt=""
            />
            <p className="tw-text-xs tw-font-medium tw-text-dark tw-m-0 tw-mb-1">
              {t('chat.new.conversation')}
            </p>
            <div className="tw-mt-3 tw-inline-flex tw-items-center tw-gap-2 tw-bg-warning-opacity tw-text-warning tw-text-[calc(var(--unit-minthr)*11)] tw-px-3 tw-py-2 tw-rounded-full">
              <img
                className="tw-w-3 tw-h-3 tw-icon-warning"
                src="/icons/lock.svg"
                alt=""
              />
              {t('chat.empty.conversation')}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default RenderMessage;
